// SPEC-ALERT-ASSIGNED-STORES-AND-NAMES-01 (D-154): 報告(booth_alerts)の配達先絞り込み + 名前引き + 現場バッジ用の取得層。
// スキーマ変更なし・RLS 変更なし・新規テーブルなし。read + 既存 resolved 更新のみ(更新は既存 Launcher 側)。
import { supabase } from '../lib/supabase'
import { fetchPrizeShortNames } from './prizeShortNames' // D-298 IMPL_5: prize_id→short_name(モジュールキャッシュ)
import { shortNameMatchesSavedName } from '../shared/lib/prizeShortNameMatch' // D-1081 追補: 保存名がマスタの景品か

// D-296 IMPL_1: 人の報告の全取得から除外する type_code(単価乖離=自動生成の meter_anomaly を分離)。
// データは消さず非表示にするだけ。除外は supabase 側 not in で、limit の前に落とす(limit が乖離で埋まらないように)。
// SPEC-ALERT-HIDE-OCR-DISAGREEMENT-FROM-FIELD-01 (D-374): OCR読取不一致(D-356 insertDisagreementAlert が作る
//   ocr_model_disagreement)はエンジン名/metric UUID を含み現場には意味不明なノイズ。行は DB に残す(管理は Supabase 直確認)
//   まま、人の報告一覧(現場帯・Launcher・admin全店・ブース履歴)からは非表示にする。meter_anomaly と同型の運用。
export const REPORT_EXCLUDED_TYPE_CODES = ['meter_anomaly', 'ocr_model_disagreement']
const EXCLUDED_IN_LIST = `(${REPORT_EXCLUDED_TYPE_CODES.join(',')})`

// SPEC-REPORT-SEVERITY-3CLASS-01 (D-343): 報告の3区分。内部コードは 停止中=stopped / 不具合=fault / 改善=idea。
//   表示ラベルは「改善」(idea の表示名。値の改名はしない)。UI_1 のブロック見出しに使う。
export const SEVERITY_LABELS = { stopped: '停止中', fault: '不具合', idea: '改善' }
export const SEVERITY_ORDER = ['stopped', 'fault', 'idea'] // 上から 停止中→不具合→改善
// 既定写像(migration の default_severity と一致)。DBの severity があればそれ優先、無ければ type_code から補完
//   (migration 未適用の develop でも動く=読み取り側フォールバックのみ。書き込みは DB トリガに任せる)。
const DEFAULT_SEVERITY_BY_TYPE = { machine_issue: 'fault', prize_shortage: 'idea', needs_check: 'fault', replace_review: 'idea', meter_anomaly: null, prize_out_anomaly: 'fault' }
// alert の区分。DB severity 優先 → 無ければ type_code 既定写像 → 写像に無い未知の type_code は安全側で 'fault'(不具合)
//   =未知の報告を黙って隠さない。明示的に null なのは meter_anomaly のみ(=3ブロックのどれにも入れない=D-296 除外と二重)。
export function severityOf(alert) {
  if (alert?.severity) return alert.severity
  const t = alert?.type_code
  return (t && t in DEFAULT_SEVERITY_BY_TYPE) ? DEFAULT_SEVERITY_BY_TYPE[t] : 'fault'
}

// ログイン中 staff の担当店舗コード。担当0件なら []（全件フォールバックはしない=呼び出し側で空扱い）。
export async function fetchAssignedStoreCodes(staffId) {
  if (!staffId) return []
  // eslint-disable-next-line supabase-guard/require-order -- LEGACY(D-330): 是正保留
  const { data } = await supabase.from('staff_stores').select('store_code').eq('staff_id', staffId)
  return [...new Set((data ?? []).map(r => r.store_code).filter(Boolean))]
}

// 与えられた store_code 群の未対応 booth_alerts（alert_types ラベル同梱）。codes 空なら空配列。
// D-295 IMPL_8: alert_types に sort_order を追加(種別チップの固定並び用)。他の列は不変。
export async function fetchUnresolvedAlertsForStores(storeCodes) {
  if (!storeCodes || storeCodes.length === 0) return []
  const { data } = await supabase
    .from('booth_alerts')
    .select('*, alert_types(label, icon_emoji, icon_key, color_hex, sort_order)')
    .eq('resolved', false)
    .in('store_code', storeCodes)
    .not('type_code', 'in', EXCLUDED_IN_LIST) // D-296 IMPL_1: 除外は limit の前
    .order('created_at', { ascending: false })
    .limit(50)
  return data ?? []
}

// D-295 IMPL_1: 全店の未対応 booth_alerts。in 句を外し limit 200(担当絞りの 50 は据置)。並びは created_at 降順で同じ。
export async function fetchAllUnresolvedAlerts() {
  const { data } = await supabase
    .from('booth_alerts')
    .select('*, alert_types(label, icon_emoji, icon_key, color_hex, sort_order)')
    .eq('resolved', false)
    .not('type_code', 'in', EXCLUDED_IN_LIST) // D-296 IMPL_1: 除外は limit の前
    .order('created_at', { ascending: false })
    .limit(200)
  return data ?? []
}

// D-301 IMPL_1: 管理ハブの報告一覧タイル用に「未対応件数だけ」を取る(行は取らない)。
// head:true + count:'exact' で件数のみ。除外は一覧と同じ EXCLUDED_IN_LIST(単価乖離 meter_anomaly はバッジにも数えない)。
export async function countUnresolvedAlerts() {
  const { count } = await supabase
    .from('booth_alerts')
    .select('alert_id', { count: 'exact', head: true })
    .eq('resolved', false)
    .not('type_code', 'in', EXCLUDED_IN_LIST)
  return count ?? 0
}

// D-295/D-297: 全店スコープ(admin/manager)判定の単一定義。一覧と Launcher で同じ分岐を使う(2重に書かない)。
export function isAllScopeRole(role) {
  return role === 'admin' || role === 'manager'
}

// ITEM_1 / D-295 IMPL_1: Launcher 用。role が admin/manager なら全店取得(scope='all')。
// それ以外(role 未指定含む=後方互換)は従来どおり担当店で絞る(担当0件は空・全件フォールバックしない)。
export async function fetchLauncherAlerts(staffId, role) {
  if (isAllScopeRole(role)) {
    const alerts = await fetchAllUnresolvedAlerts()
    return { alerts, assignedCount: null, scope: 'all' }
  }
  const codes = await fetchAssignedStoreCodes(staffId)
  if (codes.length === 0) return { alerts: [], assignedCount: 0, scope: 'assigned' }
  const alerts = await fetchUnresolvedAlertsForStores(codes)
  return { alerts, assignedCount: codes.length, scope: 'assigned' }
}

const UNSET_AREA = 'エリア未設定'

// D-295 IMPL_3: 未対応 alerts を エリア > 店舗 の2段に畳む純関数(supabase を叩かない)。
// 戻り: [{ area, count, stores: [{ store_code, store_name, count, alerts: [...] }] }]
// 並び: エリアも店舗も count 降順→同数は名前昇順。エリア未設定は count 無関係に最後。店舗内 alerts は入力順(created_at 降順)を保つ。
export function groupAlertsByAreaStore(alerts, storeMap = {}, areaMap = {}) {
  const areas = new Map() // area -> Map(store_code -> alerts[])
  for (const a of alerts ?? []) {
    if (!a?.store_code) continue
    const raw = areaMap[a.store_code]
    const area = (raw != null && String(raw).trim() !== '') ? String(raw).trim() : UNSET_AREA
    if (!areas.has(area)) areas.set(area, new Map())
    const stores = areas.get(area)
    if (!stores.has(a.store_code)) stores.set(a.store_code, [])
    stores.get(a.store_code).push(a)
  }
  const result = []
  for (const [area, storesMap] of areas) {
    const stores = []
    let areaCount = 0
    for (const [store_code, arr] of storesMap) {
      stores.push({ store_code, store_name: storeMap[store_code] ?? store_code, count: arr.length, alerts: arr })
      areaCount += arr.length
    }
    stores.sort((x, y) => (y.count - x.count) || x.store_name.localeCompare(y.store_name, 'ja'))
    result.push({ area, count: areaCount, stores })
  }
  result.sort((x, y) => {
    if (x.area === UNSET_AREA) return 1
    if (y.area === UNSET_AREA) return -1
    return (y.count - x.count) || x.area.localeCompare(y.area, 'ja')
  })
  return result
}

// D-295 IMPL_8: 未対応 alerts を種別で集計する純関数(supabase を叩かない)。
// 戻り: [{ type_code, label, icon, color, sort_order, count }]。並びは sort_order 昇順。件数0の種別は含まない(存在する種別のみ)。
// label/icon/color/sort_order は同梱の alert_types から。無い行は type_code をラベル代わり・sort_order は最後扱い。
export function countAlertsByType(alerts) {
  const byType = new Map()
  for (const a of alerts ?? []) {
    const code = a?.type_code
    if (!code) continue
    if (!byType.has(code)) {
      const t = a.alert_types || null
      byType.set(code, {
        type_code: code,
        label: t?.label ?? code,
        icon: t?.icon_emoji ?? '📌',
        iconKey: t?.icon_key ?? null, // D-308: シルエット用。null は icon(emoji)フォールバック。
        color: t?.color_hex ?? '#f87171',
        sort_order: (t?.sort_order != null) ? t.sort_order : Number.MAX_SAFE_INTEGER,
        count: 0,
      })
    }
    byType.get(code).count += 1
  }
  return [...byType.values()].sort((a, b) => (a.sort_order - b.sort_order) || a.label.localeCompare(b.label, 'ja'))
}

// D-297 IMPL_3: エリア別件数チップ。純関数。件数降順→同数は名前昇順、エリア未設定は最後。件数0(=不在)は含まれない。
export function countAlertsByArea(alerts, areaMap = {}) {
  const m = new Map()
  for (const a of alerts ?? []) {
    if (!a?.store_code) continue
    const raw = areaMap[a.store_code]
    const area = (raw != null && String(raw).trim() !== '') ? String(raw).trim() : UNSET_AREA
    m.set(area, (m.get(area) ?? 0) + 1)
  }
  const arr = [...m.entries()].map(([area, count]) => ({ area, count }))
  arr.sort((x, y) => {
    if (x.area === UNSET_AREA) return 1
    if (y.area === UNSET_AREA) return -1
    return (y.count - x.count) || x.area.localeCompare(y.area, 'ja')
  })
  return arr
}

// D-297 IMPL_3: 店舗別件数チップ。純関数。areaFilter を渡すとそのエリアの店だけに縮む。件数降順→同数は名前昇順。
export function countAlertsByStore(alerts, storeMap = {}, areaMap = {}, areaFilter = null) {
  const m = new Map()
  for (const a of alerts ?? []) {
    if (!a?.store_code) continue
    if (areaFilter != null) {
      const raw = areaMap[a.store_code]
      const area = (raw != null && String(raw).trim() !== '') ? String(raw).trim() : UNSET_AREA
      if (area !== areaFilter) continue
    }
    m.set(a.store_code, (m.get(a.store_code) ?? 0) + 1)
  }
  const arr = [...m.entries()].map(([store_code, count]) => ({ store_code, store_name: storeMap[store_code] ?? store_code, count }))
  arr.sort((x, y) => (y.count - x.count) || x.store_name.localeCompare(y.store_name, 'ja'))
  return arr
}

// D-297 IMPL_3: 行がどのエリアに属するか(areaMap 経由)。フィルタ判定用の共通ヘルパ。
export function areaOfAlert(alert, areaMap = {}) {
  const raw = alert && areaMap[alert.store_code]
  return (raw != null && String(raw).trim() !== '') ? String(raw).trim() : UNSET_AREA
}

// ITEM_2_PRIZE_SOURCE: 各 alert の景品名を引く。reading_id→meter_readings.prize_name、
//   null/引けない場合は booth_code の最新 patrol_date 行の prize_name。どちらも無ければ未設定(行を出さない)。
export async function fetchAlertPrizeNames(alerts) {
  const map = {} // alert_id -> 表示名(short_name 優先)
  if (!alerts?.length) return map

  // D-298 IMPL_5: prize_id も引く(short_name 解決用)。prize_name はフォールバック用に保持。
  const readingIds = [...new Set(alerts.map(a => a.reading_id).filter(Boolean))]
  const byReading = {} // reading_id -> {prize_id, prize_name}
  if (readingIds.length) {
    // eslint-disable-next-line supabase-guard/require-order -- LEGACY(D-330): 是正保留
    const { data } = await supabase.from('meter_readings').select('reading_id, prize_id, prize_name').in('reading_id', readingIds)
    for (const r of data ?? []) if (r.prize_name || r.prize_id) byReading[r.reading_id] = { prize_id: r.prize_id ?? null, prize_name: r.prize_name ?? null }
  }

  // reading 経由で景品名が取れなかった alert のみ booth_code フォールバック
  const needBooth = alerts.filter(a => !(a.reading_id && byReading[a.reading_id]))
  const boothCodes = [...new Set(needBooth.map(a => a.booth_code).filter(Boolean))]
  const byBooth = {} // booth_code -> {prize_id, prize_name}
  if (boothCodes.length) {
    // eslint-disable-next-line supabase-guard/require-bound -- LEGACY(D-330): 是正保留
    const { data } = await supabase
      .from('meter_readings')
      .select('booth_code, prize_id, prize_name, patrol_date, created_at')
      .in('booth_code', boothCodes)
      .order('patrol_date', { ascending: false })
      .order('created_at', { ascending: false })
    const seen = new Set()
    for (const r of data ?? []) {
      if (!r.booth_code || seen.has(r.booth_code)) continue
      seen.add(r.booth_code) // 最新 patrol_date 行のみ採用
      if (r.prize_name || r.prize_id) byBooth[r.booth_code] = { prize_id: r.prize_id ?? null, prize_name: r.prize_name ?? null }
    }
  }

  const resolved = {} // alert_id -> {prize_id, prize_name}
  for (const a of alerts) {
    const e = (a.reading_id && byReading[a.reading_id]) || byBooth[a.booth_code] || null
    if (e) resolved[a.alert_id] = e
  }

  // D-298 IMPL_5: prize_id → short_name を優先、null/空だけ prize_name にフォールバック。
  //   prize_name はクライアントで正規表現等で切り詰めない(short_name がそのための列)。
  const prizeIds = [...new Set(Object.values(resolved).map(e => e.prize_id).filter(v => v != null))]
  //   D-1081 追補: 保存名がそのマスタの正式名と一致するときだけ short_name(入替で前の景品の prize_id が残った行で
  //   前の景品の短縮名を出さない)。判定は入力欄・一覧と同じ shortNameMatchesSavedName。
  const shortMap = prizeIds.length ? await fetchPrizeShortNames(prizeIds, { withPrizeName: true }) : {}
  for (const [alertId, e] of Object.entries(resolved)) {
    const m = e.prize_id != null ? shortMap[e.prize_id] : null
    const name = (m && shortNameMatchesSavedName({ shortName: m.shortName, savedName: e.prize_name, masterPrizeName: m.prizeName }))
      ? m.shortName : e.prize_name
    if (name) map[alertId] = name
  }
  return map
}

// 純関数: 未対応 alerts を booth_code 別に畳む（バッジ/帯用に必要な最小フィールドへ整形）。
// 担当店舗で絞らない(ITEM_3: その店に居る人には必ず見せる)。
export function groupAlertsByBooth(alerts) {
  const byBooth = {}
  for (const a of alerts ?? []) {
    if (!a?.booth_code) continue
    ;(byBooth[a.booth_code] ??= []).push({
      alert_id: a.alert_id,
      label: a.alert_types?.label ?? a.type_code,
      icon: a.alert_types?.icon_emoji ?? '📌',
      iconKey: a.alert_types?.icon_key ?? null, // D-308: シルエット用。null は icon(emoji)フォールバック。
      typeCode: a.type_code ?? null, // D-1010: icon_key が null の種別も種別コードから線のアイコンを引く。
      color: a.alert_types?.color_hex ?? '#f87171',
      note: a.note ?? null,
    })
  }
  return byBooth
}

// ITEM_3/ITEM_3_PERF: 店舗画面の入店時に1回だけ、その店の未対応報告を booth_code 別に取得。
export async function fetchStoreUnresolvedAlertsByBooth(storeCode) {
  if (!storeCode) return {}
  // eslint-disable-next-line supabase-guard/require-bound -- LEGACY(D-330): 是正保留
  const { data } = await supabase
    .from('booth_alerts')
    .select('alert_id, booth_code, type_code, note, alert_types(label, icon_emoji, icon_key, color_hex)')
    .eq('store_code', storeCode)
    .eq('resolved', false)
    .not('type_code', 'in', EXCLUDED_IN_LIST) // D-296 IMPL_1: 単価乖離を除外
    .order('created_at', { ascending: false })
  return groupAlertsByBooth(data ?? [])
}

// D-296 IMPL_4: そのブースの全報告履歴(resolved 問わず)。created_at 降順、meter_anomaly 除外。range でページング(もっと見る +limit)。
export async function fetchBoothReportHistory(boothCode, limit = 20, offset = 0) {
  if (!boothCode) return []
  const { data, error } = await supabase
    .from('booth_alerts')
    .select('alert_id, type_code, note, created_at, created_by, resolved, resolved_at, resolved_note, alert_types(label, icon_emoji, icon_key, color_hex)')
    .eq('booth_code', boothCode)
    .not('type_code', 'in', EXCLUDED_IN_LIST) // D-296 IMPL_1/AC12: 履歴にも単価乖離を含めない
    .order('created_at', { ascending: false })
    .range(offset, offset + limit - 1)
  if (error) throw error
  return data ?? []
}

// SPEC-RBAC-CONTRACTOR-ALERTS-01 (D-310) S5 / D-410 ITEM_6 / D-1130: 「対応完了」を押せるか。AlertListPage から移した(Launcher が
//   lazy の AlertListPage を最初の chunk に引かないため)。AlertListPage は同じ名前で re-export する(D-1129 の import も不変)。
//   contractor はマスタ(alert_types.contractor_resolvable)依存・未ロード(null)は安全側で出さない。prize_out_anomaly は管理者のみ。
const ADMIN_ONLY_RESOLVE = new Set(['prize_out_anomaly'])
export function contractorCanResolve(role, typeCode, resolvableCodes) {
  if (ADMIN_ONLY_RESOLVE.has(typeCode)) return role === 'admin'
  if (role !== 'contractor') return true
  return resolvableCodes instanceof Set ? resolvableCodes.has(typeCode) : false
}

// D-1137: 報告 1 件の「対応完了」と「元に戻す」の書き込み。更新の形は Launcher の handleResolveAlert(完了)・
//   handleUndoResolve(元に戻す)と同じ(resolved / resolved_at / resolved_by を alert_id 1 件に)。失敗は throw して呼び出し側が出す。
//   ブース画面の帯の ✓(BoothAlertBanner)が使う。Launcher・AlertListPage・AlertSheetModal をここへ寄せるのは別 D。
export async function resolveBoothAlert(alertId, staffId) {
  if (!alertId) throw new Error('alertId is required')
  const { error } = await supabase.from('booth_alerts').update({
    resolved:    true,
    resolved_at: new Date().toISOString(),
    resolved_by: staffId ?? null,
  }).eq('alert_id', alertId)
  if (error) throw error
}

export async function unresolveBoothAlert(alertId) {
  if (!alertId) throw new Error('alertId is required')
  const { error } = await supabase.from('booth_alerts').update({
    resolved: false, resolved_at: null, resolved_by: null,
  }).eq('alert_id', alertId)
  if (error) throw error
}
